import { newE2EPage } from "@stencil/core/testing";

const htmlSingleSelection = `<uic-dropdown>
<uic-list-item value="1">Item 1</uic-list-item>
<uic-list-item value="2">Item 2</uic-list-item>
<uic-list-item value="3">Item 3</uic-list-item>
</uic-dropdown>`;

const htmlMultipleSelection = `<uic-dropdown multiple>
<uic-check-list-item value="1">Item 1</uic-check-list-item>
<uic-check-list-item value="2">Item 2</uic-check-list-item>
<uic-check-list-item value="3">Item 3</uic-check-list-item>
</uic-dropdown>`;

const htmlControllerSelection = `<uic-dropdown multiple>
<uic-check-list-item uic-controller value="all">All</uic-check-list-item>
<uic-check-list-item value="1">Item 1</uic-check-list-item>
<uic-check-list-item value="2">Item 2</uic-check-list-item>
<uic-check-list-item value="3">Item 3</uic-check-list-item>
</uic-dropdown>`;

const htmlIconSelection = `<uic-dropdown variant="header-icon-only">
<uic-icon slot="selected-icon" name="uic:square-line-cut"></uic-icon>
<uic-list-item value="1" icon="uic:apps">Item 1</uic-list-item>
<uic-list-item value="2" icon="uic:edit">Item 2</uic-list-item>
</uic-dropdown>`;

const htmlNoItems = `<uic-dropdown value="1" placeholder="none"></uic-dropdown>`;

describe("uic-dropdown", () => {
    describe("basics", () => {
        xit("should render", async () => {
            const page = await newE2EPage({
                html: htmlSingleSelection
            });

            const component = await page.find("uic-dropdown");
            await page.waitForChanges();
            await page.evaluate(() => {
                // remove simplebar (inside uic-dropdown) to make snapshot testing easier
                document.querySelector("uic-dropdown").shadowRoot.querySelector("uic-scrollpanel").innerHTML = "";
            });
            await page.waitForChanges();
            expect(component).toMatchSnapshot();
        });

        it("should not emit uicChange when setting value programmatically", async () => {
            const page = await newE2EPage({
                html: htmlSingleSelection
            });

            const component = await page.find("uic-dropdown");
            const uicChange = await component.spyOnEvent("uicChange");
            component.setProperty("value", "1");

            await page.waitForChanges();

            expect(uicChange).toHaveReceivedEventTimes(0);
        });

        it("should emit uicChange when selecting an item", async () => {
            const page = await newE2EPage({
                html: htmlSingleSelection
            });

            const component = await page.find("uic-dropdown");
            const uicChange = await component.spyOnEvent("uicChange");

            // open panel
            component.click();
            await page.waitForChanges();

            // select item by click
            const listItem = await page.find("uic-list-item");
            listItem.click();

            await page.waitForChanges();

            expect(uicChange).toHaveReceivedEventTimes(1);
        });
    });

    describe("multiselection", () => {
        it("should select multiple items", async () => {
            const page = await newE2EPage({
                html: htmlMultipleSelection
            });

            const component = await page.find("uic-dropdown");
            component.setProperty("value", ["2", "3"]);

            await page.waitForChanges();
            const items = await page.findAll("uic-check-list-item[selected]");
            expect(items.length).toBe(2);

            const item2Value = await items[0].getProperty("value");
            const item3Value = await items[1].getProperty("value");

            expect(item2Value).toEqual("2");
            expect(item3Value).toEqual("3");
        });

        it("should select all items of a controller", async () => {
            const page = await newE2EPage({
                html: htmlControllerSelection
            });

            const component = await page.find("uic-dropdown");
            component.setProperty("value", ["all"]);

            await page.waitForChanges();
            const items = await page.findAll("uic-check-list-item[selected]");
            expect(items.length).toBe(4);
        });
    });

    describe("keyboard navigation", () => {
        it("should be able to select an item", async () => {
            const page = await newE2EPage({
                html: htmlSingleSelection
            });

            const component = await page.find("uic-dropdown");
            // tab to component
            await page.keyboard.press("Tab");

            // open panel
            await page.keyboard.press("ArrowDown");

            // wait for panel
            await page.waitForChanges();

            // move down
            await page.keyboard.press("ArrowDown");
            // select item
            await page.keyboard.press("Enter");

            await page.waitForChanges();

            expect(await component.getProperty("value")).toBe("2");
        });
    });

    describe("timing", () => {
        it("keeps its value even though related option is missing", async () => {
            const page = await newE2EPage({ html: htmlNoItems });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            expect(await dropdown.getProperty("value")).toEqual("1");
            expect((await dropdown.findAll("uic-list-item")).length).toEqual(0);
            expect(dropdownTrigger.textContent.trim()).toEqual("none");
        });

        it("displays selected item's label on instantly constructing list and its items", async () => {
            const page = await newE2EPage({
                html: htmlSingleSelection.replace(/<uic-dropdown>/, `<uic-dropdown value="2">`)
            });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            const items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("1");
            expect(await items[1].getProperty("value")).toEqual("2");
            expect(await items[2].getProperty("value")).toEqual("3");
            expect(await dropdown.getProperty("value")).toEqual("2");
            expect(dropdownTrigger.textContent).toEqual("Item 2");
        });

        it("detects deferred addition of list item with value matching current one of list", async () => {
            const page = await newE2EPage({ html: htmlNoItems });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            const itemAdded = await dropdown.spyOnEvent("uicItemAdded");
            const itemUpdated = await dropdown.spyOnEvent("uicItemUpdated");

            let items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(0);

            // add list item with mismatching value
            // -> do not expect any change to dropdown or its trigger button
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.value = "1";
                item.textContent = "Foo";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(1);
            expect(itemUpdated).not.toHaveReceivedEvent();

            items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(1);
            expect(await items[0].getProperty("value")).toEqual("1");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("Foo");
        });

        it("detects late addition of list item with value matching current one of list", async () => {
            const page = await newE2EPage({ html: htmlNoItems });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            const itemAdded = await dropdown.spyOnEvent("uicItemAdded");
            const itemUpdated = await dropdown.spyOnEvent("uicItemUpdated");

            // add list item with mismatching value
            // -> do not expect any change to dropdown or its trigger button
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.value = "2";
                item.textContent = "Foo";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(1);
            expect(itemUpdated).not.toHaveReceivedEvent();

            let items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(1);
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("none");

            // add list item with matching value
            // -> expect label of trigger button to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.value = "1";
                item.textContent = "bar!";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(2);
            expect(itemUpdated).not.toHaveReceivedEvent();

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");

            // add list item with another non-matching value
            // -> expect label of trigger button not to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.value = "3";
                item.textContent = "baz";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(3);
            expect(itemUpdated).not.toHaveReceivedEvent();

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await items[2].getProperty("value")).toEqual("3");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");

            // add another list item with matching value
            // -> expect label of trigger button not to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.value = "1";
                item.textContent = "bam!";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(4);
            expect(itemUpdated).not.toHaveReceivedEvent();

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await items[2].getProperty("value")).toEqual("3");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");
        });

        it("detects deferred addition of list item with value matching current one of list defined after that", async () => {
            const page = await newE2EPage({ html: htmlNoItems });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            const itemAdded = await dropdown.spyOnEvent("uicItemAdded");
            const itemUpdated = await dropdown.spyOnEvent("uicItemUpdated");

            let items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(0);

            // add list item with mismatching value
            // -> do not expect any change to dropdown or its trigger button
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.textContent = "Foo";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                dropdown.querySelector("uic-list-item:last-child").value = "1";
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(1);
            expect(itemUpdated).toHaveReceivedEventTimes(1);

            items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(1);
            expect(await items[0].getProperty("value")).toEqual("1");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("Foo");
        });

        it("detects late addition of list item with value matching current one of list assigned after that", async () => {
            const page = await newE2EPage({ html: htmlNoItems });

            const dropdown = await page.find("uic-dropdown");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");

            const itemAdded = await dropdown.spyOnEvent("uicItemAdded");
            const itemUpdated = await dropdown.spyOnEvent("uicItemUpdated");

            // add list item with mismatching value
            // -> do not expect any change to dropdown or its trigger button
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.textContent = "Foo";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();

            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                dropdown.querySelector("uic-list-item:last-child").value = "2";
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(1);
            expect(itemUpdated).toHaveReceivedEventTimes(1);

            let items = await dropdown.findAll("uic-list-item");
            expect(items.length).toEqual(1);
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("none");

            // add list item with matching value
            // -> expect label of trigger button to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.textContent = "bar!";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                dropdown.querySelector("uic-list-item:last-child").value = "1";
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(2);
            expect(itemUpdated).toHaveReceivedEventTimes(2);

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");

            // add list item with another non-matching value
            // -> expect label of trigger button not to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.textContent = "baz";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                dropdown.querySelector("uic-list-item:last-child").value = "3";
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(3);
            expect(itemUpdated).toHaveReceivedEventTimes(3);

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await items[2].getProperty("value")).toEqual("3");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");

            // add another list item with matching value
            // -> expect label of trigger button not to change
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                const item = dropdown.ownerDocument.createElement("uic-list-item");
                item.textContent = "bam!";

                dropdown.appendChild(item);
            });

            await page.waitForChanges();
            // eslint-disable-next-line
            await page.$eval("uic-dropdown", (dropdown: any) => {
                dropdown.querySelector("uic-list-item:last-child").value = "1";
            });

            await page.waitForChanges();

            expect(itemAdded).toHaveReceivedEventTimes(4);
            expect(itemUpdated).toHaveReceivedEventTimes(4);

            items = await dropdown.findAll("uic-list-item");
            expect(await items[0].getProperty("value")).toEqual("2");
            expect(await items[1].getProperty("value")).toEqual("1");
            expect(await items[2].getProperty("value")).toEqual("3");
            expect(await dropdown.getProperty("value")).toEqual("1");
            expect(dropdownTrigger.textContent).toEqual("bar!");
        });
    });

    describe("header-icon variants", () => {
        it("should display the selected-icon slot if no item is selected", async () => {
            const page = await newE2EPage({
                html: htmlIconSelection
            });
            const component = await page.find("uic-dropdown");
            await page.waitForChanges();
            expect(await component.getProperty("value")).toBe(undefined);
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");
            expect(dropdownTrigger).toMatchSnapshot();
        });

        it("should display the icon of the initially selected list item", async () => {
            const page = await newE2EPage({
                html: htmlIconSelection.replace("<uic-dropdown ", `<uic-dropdown value="2" `)
            });
            const component = await page.find("uic-dropdown");
            await page.waitForChanges();
            expect(await component.getProperty("value")).toBe("2");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");
            expect(dropdownTrigger).toMatchSnapshot();
        });

        it("should display the according icon if a list item is selected", async () => {
            const page = await newE2EPage({
                html: htmlIconSelection
            });
            const component = await page.find("uic-dropdown");
            await page.waitForChanges();
            expect(await component.getProperty("value")).toBe(undefined);
            component.setProperty("value", "1");
            await page.waitForChanges();
            expect(await component.getProperty("value")).toBe("1");
            const dropdownTrigger = await page.find("uic-dropdown >>> *[slot=trigger]");
            expect(dropdownTrigger).toMatchSnapshot();
        });
    });
});
